import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MessageInput = ({ onSend }) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const handleSend = async () => {
    if (!message?.trim() && attachments?.length === 0) return;

    setIsSending(true);
    await onSend(message, attachments);
    setMessage('');
    setAttachments([]);
    setIsSending(false);
    
    // Reset textarea height
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const handleTextareaChange = (e) => {
    setMessage(e?.target?.value);
    
    // Auto-resize textarea
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef?.current?.scrollHeight, 120)}px`;
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    const newAttachments = files?.map(file => ({
      name: file?.name,
      size: `${(file?.size / 1024)?.toFixed(1)} KB`,
      type: file?.type?.startsWith('image/') ? 'image' : 'file',
      url: URL.createObjectURL(file)
    }));
    setAttachments([...attachments, ...newAttachments]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments?.filter((_, i) => i !== index));
  };

  return (
    <div className="p-4 md:p-6">
      {/* Attachments Preview */}
      {attachments?.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {attachments?.map((attachment, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-2 bg-[#F6F7FB] rounded-lg text-sm"
            >
              <Icon name={attachment?.type === 'image' ? 'Image' : 'FileText'} size={16} color="#6B7280" />
              <span className="text-[#111827] truncate max-w-[150px]">{attachment?.name}</span>
              <button
                onClick={() => removeAttachment(index)}
                className="p-0.5 hover:bg-[#E5E7EB] rounded transition-colors"
                aria-label="Remove attachment"
              >
                <Icon name="X" size={14} color="#6B7280" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input Area */}
      <div className="flex items-end gap-2 md:gap-3">
        {/* File Attachment Button */}
        <button
          onClick={() => fileInputRef?.current?.click()}
          className="flex-shrink-0 p-2 hover:bg-[#F6F7FB] rounded-lg transition-colors"
          aria-label="Attach file"
        >
          <Icon name="Paperclip" size={20} color="#6B7280" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Message Textarea */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextareaChange}
            onKeyPress={handleKeyPress}
            placeholder="Type your message... (Shift+Enter for new line)"
            className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#5B2DFF] focus:border-transparent"
            rows={1}
            style={{ minHeight: '44px', maxHeight: '120px' }}
          />
        </div>

        {/* Send Button */}
        <Button
          variant="default"
          size="default"
          iconName="Send"
          loading={isSending}
          onClick={handleSend}
          disabled={!message?.trim() && attachments?.length === 0}
          className="flex-shrink-0"
        >
          <span className="hidden sm:inline">Send</span>
        </Button>
      </div>

      {/* Helper Text */}
      <div className="mt-2 flex items-center gap-4 text-xs text-[#6B7280]">
        <span>Press Enter to send, Shift+Enter for new line</span>
        <span>•</span>
        <span>Max file size: 5MB</span>
      </div>
    </div>
  );
};

export default MessageInput;